import { Injectable, inject } from '@angular/core';
import { OidcSecurityService, LoginResponse } from 'angular-auth-oidc-client';
import { Observable, map, filter, switchMap, of } from 'rxjs';

export interface UserInfo {
  sub: string;
  name?: string;
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oidcSecurityService = inject(OidcSecurityService);

  // Observable indicating whether user is authenticated
  isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
    map((result) => result.isAuthenticated)
  );

  // Observable with current access token
  accessToken$ = this.oidcSecurityService.getAccessToken();

  // Observable with current ID token
  idToken$ = this.oidcSecurityService.getIdToken();

  // Observable with user information from ID token
  userData$: Observable<UserInfo | null> = this.oidcSecurityService.userData$.pipe(
    map((result) => result.userData as UserInfo | null)
  );

  // Check authentication status and handle callback
  checkAuth(): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth();
  }

  // Start login flow (redirects to Keycloak)
  login(): void {
    this.oidcSecurityService.authorize();
  }

  // Logout user (redirects to Keycloak logout)
  logout(): void {
    this.oidcSecurityService.logoff().subscribe();
  }

  // Get access token synchronously (for HTTP interceptor)
  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  // Get ID token (for NavVis IVION API)
  getIdToken(): Observable<string> {
    return this.oidcSecurityService.getIdToken();
  }

  // Force token refresh
  refreshToken(): Observable<LoginResponse> {
    return this.oidcSecurityService.forceRefreshSession();
  }

  // Get authorization header for API calls
  getAuthorizationHeader(): Observable<Record<string, string>> {
    return this.accessToken$.pipe(
      map((token) => (token ? { Authorization: `Bearer ${token}` } : {}))
    );
  }

  // Check if user has specific role (from Keycloak token)
  hasRole(role: string): Observable<boolean> {
    return this.userData$.pipe(
      map((userData) => {
        if (!userData) return false;
        // Keycloak stores roles in realm_access or resource_access claims
        const roles = (userData as any)?.realm_access?.roles || [];
        return roles.includes(role);
      })
    );
  }
}
